import { TextNodeType, TRAVELLER_NUMBER_CONSTANTS } from "@tm/isomorphic-utils";
import { useCallback } from "react";
import FormattedText from "../../../FormattedText/FormattedText";
import {
  SelectorTrackingSource,
  trackSelectorEvent,
} from "../../shared/MwebSelectorTracking";
import {
  Container,
  CounterBox,
  CounterButton,
  CounterValue,
  CountText,
  HelpText,
  InfoSection,
  Label,
  Wrapper,
} from "./Styles";

interface TravellerSelectorProps {
  travellerInfo?: {
    label?: TextNodeType | string;
    subTitle?: TextNodeType | string;
    min?: number;
    max?: number;
  };
  travellerCount?: number;
  onTravellerCountChange?: (count: number) => void;
  /** Which page's analytics to fire. Defaults to common landing. */
  trackingSource?: SelectorTrackingSource;
}

export const TravellerSelector = ({
  travellerInfo,
  travellerCount = 1,
  onTravellerCountChange,
  trackingSource = "common_landing",
}: TravellerSelectorProps) => {
  const minAllowed =
    travellerInfo?.min ?? TRAVELLER_NUMBER_CONSTANTS.MIN_TRAVELLERS;
  const maxAllowed =
    travellerInfo?.max ?? TRAVELLER_NUMBER_CONSTANTS.MAX_TRAVELLERS;

  const isAtMin = travellerCount <= minAllowed;
  const isAtMax = travellerCount >= maxAllowed;

  const handleIncrement = useCallback(() => {
    if (travellerCount < maxAllowed) {
      onTravellerCountChange?.(travellerCount + 1);
      trackSelectorEvent(trackingSource, "traveller_count_selected", {
        payload: { travellers: String(travellerCount + 1) },
      });
    }
  }, [travellerCount, maxAllowed, onTravellerCountChange, trackingSource]);

  const handleDecrement = useCallback(() => {
    if (travellerCount > minAllowed) {
      onTravellerCountChange?.(travellerCount - 1);
      trackSelectorEvent(trackingSource, "traveller_count_selected", {
        payload: { travellers: String(travellerCount - 1) },
      });
    }
  }, [travellerCount, minAllowed, onTravellerCountChange, trackingSource]);

  return (
    <Wrapper>
      <Container>
        <InfoSection>
          <Label>
            <FormattedText
              value={travellerInfo?.label || "NO. OF TRAVELLERS"}
            />
          </Label>
          <CountText>
            {travellerCount} {travellerCount === 1 ? "Traveller" : "Travellers"}
          </CountText>
          {travellerInfo?.subTitle ? (
            <HelpText>
              <FormattedText value={travellerInfo.subTitle} />
            </HelpText>
          ) : null}
        </InfoSection>

        <CounterBox>
          <CounterButton
            type="button"
            disabled={isAtMin}
            onClick={handleDecrement}
            aria-label="Decrease traveller count"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6 11.0938H17.5V12.9064H6V11.0938Z"
                fill="#000000"
              />
            </svg>
          </CounterButton>
          <CounterValue>{travellerCount}</CounterValue>
          <CounterButton
            type="button"
            disabled={isAtMax}
            onClick={handleIncrement}
            aria-label="Increase traveller count"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.612 6.04785V11.1719H17.496V12.7919H12.612V17.9519H10.86V12.7919H6V11.1719H10.86V6.04785H12.612Z"
                fill="#000000"
              />
            </svg>
          </CounterButton>
        </CounterBox>
      </Container>
    </Wrapper>
  );
};
